import { ChangeEvent, CSSProperties, FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import { contrastColor, isValidHex, normalizeHex } from '../../../colorUtils';
import { usePopoverDismiss } from '../usePopoverDismiss';
import { ColorArea } from './ColorArea';
import styles from './PunchupColorPicker.module.css';

export interface PunchupColorPickerProps {
	value: string;
	onChange: (color: string) => void;
	/**
	 * Display colour to PREVIEW when no colour is set — the stable palette colour this entity is already
	 * shown with elsewhere (resolved via `useTicketTypeColors`). View-only: it tints the trigger swatch
	 * and labels it "Display color", making clear the colour is shown for display only and NOT persisted —
	 * `value` stays empty and `onChange` never fires until the user actually picks. Omit to keep the plain
	 * hatched "Pick a color" empty state.
	 */
	autoColor?: string;
}

/**
 * 20 preset tag colors in a 2×10 grid.
 * Row 1 — vivid/mid: red, lime, green, teal, cyan, blue, violet, pink, grey, dark grey.
 * Row 2 — deep/dark variants of the same hues.
 * Orange and amber hues are excluded — too close to the site primary (#FF5E2A).
 */
export const PRESET_COLORS = [
	// Row 1 — vivid
	'#EF4444',
	'#84CC16',
	'#22C55E',
	'#14B8A6',
	'#06B6D4',
	'#3B82F6',
	'#8B5CF6',
	'#EC4899',
	'#6B7280',
	'#374151',
	// Row 2 — deep / dark variants
	'#B91C1C',
	'#4D7C0F',
	'#166534',
	'#0F766E',
	'#155E75',
	'#1E40AF',
	'#5B21B6',
	'#9D174D',
	'#9CA3AF',
	'#111827',
] as const;

/**
 * Forces a raw input string into the custom field's display grammar: a locked leading `#` followed
 * by up to 6 hex digits. Any other characters — including a `#` the user types or a non-hex letter —
 * are stripped, so the `#` can never be deleted and only valid hex digits can be entered. A 1/2/4/5-
 * digit result is incomplete (the field shows a validation message); 3 or 6 digits is a valid colour.
 */
const toHexField = (raw: string): string => `#${raw.replace(/[^0-9a-fA-F]/g, '').slice(0, 6)}`;

/**
 * A 20px colour swatch — shows `value`, or a hatched "none selected" state when no colour is set.
 * Shared by the trigger and the custom-row preview so the two stay identical. Decorative
 * (aria-hidden): the adjacent text label / hex field carries the value for screen readers.
 */
const Swatch: FC<{ className: string; value: string }> = ({ className, value }) => (
	<span
		className={className}
		style={value ? { backgroundColor: value } : undefined}
		data-empty={value ? undefined : true}
		aria-hidden
	/>
);

/**
 * PunchupColorPicker
 *
 * A compact swatch trigger that opens a popover (above the trigger, portalled to body so it is
 * never clipped by an accordion/form overflow). The panel is a flex row: a left column with the
 * 20-preset grid + a custom hex field (and a live swatch of the current colour), and — to its
 * right — a full HSV colour area (saturation/brightness square + hue slider). All three inputs
 * stay in sync: clicking a swatch, typing a valid hex, and dragging the area all set the same
 * `value` and reflect each other. Custom colours are intentionally unrestricted — the orange/amber
 * exclusion curates the PRESETS only.
 *
 * No native OS `<input type="color">` (unstyleable, ignores tokens). Same `value: string` /
 * `onChange` contract as before, so callers (PunchupTagManager, the ticket-type colour field)
 * are unchanged.
 */
export const PunchupColorPicker: FC<PunchupColorPickerProps> = ({ value, onChange, autoColor }) => {
	const [open, setOpen] = useState(false);
	const [panelStyle, setPanelStyle] = useState<CSSProperties>({});
	// `draft` holds the live custom-field text — always `#` + up-to-6 hex digits (see `toHexField`).
	// While focused we don't let an external value change overwrite it; on blur it re-syncs to the
	// committed `value` (an incomplete draft reverts, so a broken hex is never left behind or emitted).
	const [draft, setDraft] = useState(() => toHexField(value));
	const [focused, setFocused] = useState(false);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!focused) setDraft(toHexField(value));
	}, [value, focused]);

	// Anchor the popover above the trigger (fixed; left-aligned; its bottom sits 6px above the
	// trigger top, so it grows upward). Recomputed each open.
	const updatePosition = () => {
		if (!triggerRef.current) return;
		const rect = triggerRef.current.getBoundingClientRect();
		setPanelStyle({ left: rect.left, bottom: window.innerHeight - rect.top + 6 });
	};

	// Outside click dismisses; scroll/resize RE-ANCHORS the panel rather than closing it — closing on
	// those events dismissed the picker mid-entry inside the height-synced iframe (the body-mutation→
	// height→iframe-resize loop and a focus scroll-into-view both fired here). See usePopoverDismiss.
	usePopoverDismiss({
		open,
		wrapperRef,
		panelRef,
		onDismiss: () => setOpen(false),
		onReposition: updatePosition,
	});

	// Escape closes the picker (dialog convention; kept separate from the shared dismiss hook).
	useEffect(() => {
		if (!open) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setOpen(false);
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [open]);

	// Keep the (wide + tall, row-layout) panel inside the iframe. The panel anchors above the trigger
	// and at its left edge by default. Horizontal: if that overflows the right viewport edge, shift it
	// left. Vertical: the panel grows upward, so the now-taller HSV panel can clip above the viewport
	// top when the trigger sits near the top — in that case flip to anchor below the trigger instead.
	// Runs once per open, before paint — no visible jump.
	useLayoutEffect(() => {
		if (!open || !panelRef.current || !triggerRef.current) return;
		const panel = panelRef.current.getBoundingClientRect();
		const trigger = triggerRef.current.getBoundingClientRect();
		const overflowRight = panel.right - (window.innerWidth - 8);
		const clipsTop = panel.top < 8;
		if (overflowRight <= 0 && !clipsTop) return;
		setPanelStyle((s) => {
			const next: CSSProperties = { ...s };
			if (overflowRight > 0) {
				next.left = Math.max(8, (typeof s.left === 'number' ? s.left : panel.left) - overflowRight);
			}
			if (clipsTop) {
				next.top = trigger.bottom + 6;
				delete next.bottom;
			}
			return next;
		});
	}, [open]);

	const handleToggle = () => {
		if (!open) updatePosition();
		setOpen((v) => !v);
	};

	const handleHexChange = (e: ChangeEvent<HTMLInputElement>) => {
		const next = toHexField(e.target.value);
		setDraft(next);
		if (isValidHex(next)) onChange(normalizeHex(next));
	};

	// On blur, re-sync the field to the committed colour — an incomplete/partial draft reverts so a
	// broken hex is never left in the box (the inline message guides completion while still focused).
	const handleBlur = () => {
		setFocused(false);
		setDraft(toHexField(value));
	};

	const hasValue = !!value;
	// No colour set, but a display colour to preview → tint the trigger swatch with it and label it
	// "Display color" (view-only). Purely visual: `value` stays empty and `onChange` is untouched until
	// the user actually picks, so the preview can never be mistaken for a committed choice.
	const showingAuto = !hasValue && !!autoColor;
	const triggerSwatchColor = hasValue ? value : autoColor ?? '';
	// Hex digits typed after the locked `#`. 0 = empty (neutral); 3 or 6 = valid; anything else is
	// an incomplete entry that surfaces the inline validation message.
	const hexDigitCount = draft.length - 1;
	const hexIncomplete = hexDigitCount > 0 && hexDigitCount !== 3 && hexDigitCount !== 6;

	const panel = open
		? ReactDOM.createPortal(
				<div ref={panelRef} className={styles.panel} style={panelStyle} role="dialog" aria-label="Color picker">
					<div className={styles.leftColumn}>
						<div className={styles.grid}>
							{PRESET_COLORS.map((color) => {
								const selected = value.toUpperCase() === color;
								return (
									<button
										key={color}
										type="button"
										className={styles.cell}
										style={{ backgroundColor: color }}
										onClick={() => onChange(color)}
										aria-label={color}
										aria-pressed={selected}
									>
										{selected && <Check size={12} strokeWidth={3} color={contrastColor(color)} aria-hidden />}
									</button>
								);
							})}
						</div>
						<div className={styles.customRow}>
							<span className={styles.customLabel}>Custom</span>
							<Swatch className={styles.customSwatch} value={value} />
							<input
								type="text"
								className={styles.hexInput}
								value={draft}
								onChange={handleHexChange}
								onFocus={() => setFocused(true)}
								onBlur={handleBlur}
								placeholder="#000000"
								aria-label="Custom color hex"
								aria-invalid={hexIncomplete || undefined}
								spellCheck={false}
								maxLength={7}
							/>
						</div>
						{hexIncomplete && <span className="punchupFormError">Enter a 3- or 6-digit hex colour</span>}
					</div>
					<ColorArea value={value} onChange={onChange} />
				</div>,
				document.body
		  )
		: null;

	return (
		<div className={styles.wrapper} ref={wrapperRef}>
			<button
				ref={triggerRef}
				type="button"
				className={[styles.trigger, open ? styles.triggerOpen : ''].filter(Boolean).join(' ')}
				onClick={handleToggle}
				aria-haspopup="dialog"
				aria-expanded={open}
				aria-label="Select color"
			>
				<Swatch className={styles.triggerSwatch} value={triggerSwatchColor} />
				<span className={hasValue ? styles.triggerValue : styles.triggerPlaceholder}>
					{hasValue ? value.toUpperCase() : showingAuto ? 'Display color' : 'Pick a color'}
				</span>
				<ChevronDown size={16} className={[styles.chevron, open ? styles.chevronOpen : ''].filter(Boolean).join(' ')} />
			</button>
			{panel}
		</div>
	);
};
